
import { supabase } from "@/integrations/supabase/client";
import { NotificationPreferences } from "@/types/notifications";

export const updateNotificationPreferences = async (
  userId: string,
  preferences: Partial<NotificationPreferences>
) => {
  // Check if email or SMS notifications are being enabled
  if (preferences.email_notifications === true || preferences.sms_notifications === true) {
    try {
      // Get user profile information for notification context
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('full_name, phone_number')
        .eq('id', userId)
        .single();
      
      if (profileError) {
        console.error('Error fetching profile:', profileError);
        // Continue with update even if profile fetch fails
      }
      
      // No need to get user email from auth - simply use the profile data
      // This avoids the admin.getUserById call that requires service role
      
      // Only attempt to send test notification if we have profile data
      if (profileData) {
        // Send a test notification based on the enabled channel
        const testPayload = {
          to: preferences.email_notifications ? "user@example.com" : undefined, // Placeholder email 
          subject: "Notification Settings Updated",
          notificationType: "settings_update",
          preferSMS: preferences.sms_notifications === true,
          phone: preferences.sms_notifications === true ? profileData?.phone_number : undefined,
          data: {
            message: `Hello ${profileData?.full_name || 'there'}, your notification preferences have been updated successfully.`,
            timestamp: new Date().toISOString(),
            settings: preferences,
            link: "https://yourdomain.com/account/notifications" // Example link for SMS
          }
        };

        try {
          const { error: notificationError } = await supabase.functions.invoke('send-notification', {
            body: testPayload
          });

          if (notificationError) {
            console.error('Test notification failed:', notificationError);
            // Continue with preference update even if test notification fails
          }
        } catch (error) {
          console.error('Error sending test notification:', error);
          // Continue with preference update even if test notification fails
        }
      }
    } catch (error) {
      console.error('Error during notification setup:', error);
      // Continue with preference update even if notification setup fails
    }
  }

  // Always update preferences in database regardless of notification test success
  try {
    const { data, error } = await supabase
      .from("notification_preferences")
      .update(preferences)
      .eq("user_id", userId)
      .select();

    if (error) throw error;
    return data[0] as NotificationPreferences;
  } catch (error) {
    console.error('Error updating preferences:', error);
    throw new Error("Failed to update notification preferences");
  }
};
